/**
 * Minimal HMAC-SHA256 fallback for environments where crypto.subtle is undefined
 * (e.g. Android WebView over HTTP). Used only when Web Crypto API is unavailable.
 */

const BLOCK_SIZE = 64;
const OUT_LEN = 32;

function rightRotate(value, amount) {
    return (value >>> amount) | (value << (32 - amount));
}

function sha256Bytes(data) {
    const h = new Int32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
        0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
    ]);
    const k = new Int32Array([
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
    ]);
    const len = data.length;
    const bitLen = len * 8;
    const padLen = ((len + 9 + BLOCK_SIZE) >> 6) << 6;
    const buf = new Uint8Array(padLen);
    buf.set(data);
    buf[len] = 0x80;
    const view = new DataView(buf.buffer);
    view.setUint32(padLen - 4, bitLen >>> 0, false);
    view.setUint32(padLen - 8, (bitLen / 0x100000000) >>> 0, false);

    for (let off = 0; off < padLen; off += 64) {
        const w = new Int32Array(64);
        for (let i = 0; i < 16; i++) {
            w[i] = view.getInt32(off + i * 4, false);
        }
        for (let i = 16; i < 64; i++) {
            const s0 = (rightRotate(w[i - 15], 7) ^ rightRotate(w[i - 15], 18) ^ (w[i - 15] >>> 3)) >>> 0;
            const s1 = (rightRotate(w[i - 2], 17) ^ rightRotate(w[i - 2], 19) ^ (w[i - 2] >>> 10)) >>> 0;
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, t] = h;
        for (let i = 0; i < 64; i++) {
            const S1 = (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) >>> 0;
            const ch = ((e & f) ^ ((~e) >>> 0 & g)) >>> 0;
            const temp1 = (t + S1 + ch + k[i] + w[i]) >>> 0;
            const S0 = (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) >>> 0;
            const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
            const temp2 = (S0 + maj) >>> 0;
            t = g; g = f; f = e; e = (d + temp1) >>> 0; d = c; c = b; b = a; a = (temp1 + temp2) >>> 0;
        }
        h[0] = (h[0] + a) >>> 0; h[1] = (h[1] + b) >>> 0; h[2] = (h[2] + c) >>> 0; h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0; h[5] = (h[5] + f) >>> 0; h[6] = (h[6] + g) >>> 0; h[7] = (h[7] + t) >>> 0;
    }
    const out = new Uint8Array(OUT_LEN);
    const outView = new DataView(out.buffer);
    for (let i = 0; i < 8; i++) outView.setUint32(i * 4, h[i], false);
    return out;
}

/**
 * HMAC-SHA256 sign: key and message as Uint8Array, returns 32-byte signature.
 * Used when crypto.subtle is not available (e.g. non-secure context on Android).
 */
export function hmacSha256Sign(key, message) {
    let keyBytes = key;
    if (keyBytes.length > BLOCK_SIZE) {
        keyBytes = sha256Bytes(keyBytes);
    }
    if (keyBytes.length < BLOCK_SIZE) {
        const padded = new Uint8Array(BLOCK_SIZE);
        padded.set(keyBytes);
        keyBytes = padded;
    }
    const oKeyPad = new Uint8Array(BLOCK_SIZE);
    const iKeyPad = new Uint8Array(BLOCK_SIZE);
    for (let i = 0; i < BLOCK_SIZE; i++) {
        oKeyPad[i] = keyBytes[i] ^ 0x5c;
        iKeyPad[i] = keyBytes[i] ^ 0x36;
    }
    const inner = new Uint8Array(BLOCK_SIZE + message.length);
    inner.set(iKeyPad);
    inner.set(message, BLOCK_SIZE);
    const innerHash = sha256Bytes(inner);
    const outer = new Uint8Array(BLOCK_SIZE + OUT_LEN);
    outer.set(oKeyPad);
    outer.set(innerHash, BLOCK_SIZE);
    return sha256Bytes(outer);
}
